import { makeDraggable } from '../../../components/mixins/draggable-mixin';
import { keyCodes } from '../../../lib/key-codes';
import { getTemplate } from './MainUi.html';
import {translate} from 'shared/services/I18nService';

/**
 * Generates the settings user interface. Handles data binding to settings.
 */
export class MainUi {
  /**
     * Creates a Settings UI
     * @param rootNode - the node to attach the dialog to
     * @param settingsDataBinder - binds this UI to a settings service
     * @param doSnapshot - callback to start the snapshot
     */
  constructor (rootNode, settingsDataBinder, doSnapshot) {
    let self = this;
    let container;
    let elementToAppearNear;
    let isOpen = false;
    let isVisible = false;
    const $t = translate.scope('uScope.extensions.snapshot');

    Object.assign(self, {
      // This is a bit of a hack... The tool is reshown after a snapshot is completed. Since a snapshot can be
      // started outside the tool, we should have a means to "start" and "stop" it independent of showing
      // and hiding the dialog. The open and close methods provide it.
      open () {
        isOpen = true;
        self.show();
      },

      close () {
        self.hide();
        isOpen = false;
      },

      // Like the name suggests, shows the dialog. Note that it will ONLY show if the dialog has previously
      // been opened. TODO: Maybe a better name is "restore()", but then what do we do with close() and hide()?
      show () {
        if (!isOpen || isVisible) {
          return;
        }
        isVisible = true;
        container.classList.remove('lbs-dialog-leave-active');
        container.classList.add('lbs-dialog-enter');
        positionOnce();
        rootNode.appendChild(container);
        document.addEventListener('keyup', escapeKeyHandler);

        setTimeout(() => {
          container.classList.remove('lbs-dialog-enter');
        });
      },

      // Like the name suggests, hide the dialog.
      hide () {
        if (!isVisible) {
          return;
        }
        isVisible = false;
        cleanup();
        container.classList.add('lbs-dialog-leave-active');
        listenOnce(container, 'transitionend', () => {
          if (!isVisible) {
            container.remove();
            container.classList.remove('lbs-dialog-leave-active');
          }
        });
      },

      // Shows if hidden and hides if visible.
      toggle (el) {
        elementToAppearNear = el;
        (isVisible ? self.hide : self.show)();
      }
    });

    // Only position the dialog once. After first run this function becomes a noop.
    let positionOnce = () => {
      // Try to appear near the toolbar button, if "elementToAppearNear" is defined, otherwise center on the root node.
      if (elementToAppearNear) {
        draggable.moveTo({
          top: `${elementToAppearNear.offsetParent.offsetParent.offsetTop + elementToAppearNear.offsetTop}px`,
          left: `${elementToAppearNear.offsetLeft + 30}px`
        });
      } else {
        draggable.moveTo({
          top: `${$(rootNode).height() / 2 - 157}px`,
          left: `${$(rootNode).width() / 2 - 200}px`
        });
      }
      positionOnce = () => {
      };
    };

    // Handler for when the escape key is pressed.
    const escapeKeyHandler = (e) => {
      if (e.keyCode === keyCodes.escape) {
        self.hide();
        cleanup();
      }
    };
    // Always clean up after yourself.
    function cleanup () {
      document.removeEventListener('keyup', escapeKeyHandler);
    }

    // Create the div encapsulating the user settings and apply the template.
    container = document.createElement('div');
    // Note: IE doesn't support classlist.Add() with multiple classes
    container.className += 'lbs-dialog-container snapshot-settings-dialog';
    container.innerHTML = getTemplate($t);

    // Wire up the buttons to close the dialog.
    const closeButton = container.querySelectorAll('.lbs-dialog-close-button')[0];
    closeButton.addEventListener('click', self.close);

    // Wire up the "take a snapshot" button
    const takeSnapshotButton = container.querySelectorAll('.take-snapshot-button')[0];
    takeSnapshotButton.addEventListener('click', doSnapshot);

    // Wire up the repositioning logic.
    const draggable = makeDraggable(container, container.querySelectorAll('.lbs-dialog-header')[0]);

    // Bind the settings object to the user interface.
    settingsDataBinder.bind(container, container);
  }
}

/**
 * Shorthand method for listening to a dom event only once.
 * @param element Element that event will be triggered on
 * @param event Event to listen for
 * @param callback callback to handle the event
 */
function listenOnce (element, event, callback) {
  function doCallback (e) {
    callback(e);
    element.removeEventListener(event, doCallback);
  }

  element.addEventListener(event, doCallback);
}
